
import React from "react";
import { BarChart3, MessageCircle, Users, Calendar, Zap, Star, PhoneCall, Workflow, LineChart } from "lucide-react";

export const Features: React.FC = () => {
  return (
    <section id="features" className="py-20 md:py-32 bg-secondary/30">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 fade-in">
          <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium bg-primary/10 text-primary rounded-full">
            The Complete System
          </span>
          <h2 className="text-4xl md:text-5xl font-light text-foreground mb-6">
            Every customer conversation, every channel, one intelligent system
          </h2>
          <p className="text-xl text-muted-foreground">
            Vail unifies all your customer communications—text, email, phone, SMS, WhatsApp, website chat—into one coherent record. Your AI and human teams always have complete context, and workflows happen automatically at any scale.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-16">
          <div className="fade-in">
            <div className="subtle-card p-8 h-full">
              <div className="feature-icon mb-6">
                <MessageCircle className="h-6 w-6" />
              </div>
              <h3 className="text-2xl font-medium text-foreground mb-4">Quit Chasing Emails, Texts, and Phone Calls</h3>
              <p className="text-muted-foreground mb-4">
                Every customer interaction—whether through SMS, email, phone calls, WhatsApp, or website chat—flows into one unified system. Your AI always has the complete conversation history across all channels to provide intelligent, contextual responses.
              </p>
            </div>
          </div>

          <div className="fade-in" style={{ transitionDelay: "100ms" }}>
            <div className="subtle-card p-8 h-full">
              <div className="feature-icon mb-6">
                <Users className="h-6 w-6" />
              </div>
              <h3 className="text-2xl font-medium text-foreground mb-4">Automate Everything After the Conversation</h3>
              <p className="text-muted-foreground mb-4">
                When customers ask to schedule meetings, need documents collected, want package updates, or have complaints—Vail handles it all automatically. Scale any workflow 24/7 without adding staff or missing requests.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1 fade-in">
            <span className="inline-block px-4 py-1.5 mb-4 text-sm font-medium bg-primary/10 text-primary rounded-full">
              Beyond Communication Management
            </span>
            <h3 className="text-3xl font-light text-foreground mb-6">
              Complete workflow automation that works with your tools
            </h3>
            <p className="text-muted-foreground mb-8">
              Vail doesn't just track conversations—it connects to your existing systems in real-time. When customers need something, we look it up, process it, and take action. Your team gets complete context, and your customers get instant results.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start">
                <div className="feature-icon shrink-0 mr-4 !w-10 !h-10">
                  <Calendar className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="text-foreground font-medium mb-1">Complete Context</h4>
                  <p className="text-muted-foreground text-sm">Every conversation, every request, every interaction—all available to AI and humans instantly</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="feature-icon shrink-0 mr-4 !w-10 !h-10">
                  <Star className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="text-foreground font-medium mb-1">Real-Time Tool Integration</h4>
                  <p className="text-muted-foreground text-sm">Connect to calendars, CRMs, payment systems—Vail works with what you already use</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="order-1 lg:order-2 fade-in">
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-md aspect-[4/3]">
                <img 
                  src="https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?auto=format&fit=crop&w=800&q=80" 
                  alt="Technology dashboard" 
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 glass-card p-4 shadow-glass animate-float">
                <div className="flex items-center gap-3">
                  <PhoneCall className="h-8 w-8 text-primary" />
                  <div>
                    <div className="text-foreground font-medium">Sales Team Ready</div>
                    <div className="text-muted-foreground text-sm">Qualified prospects, full context</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="subtle-card p-8 fade-in">
            <div className="feature-icon mb-5">
              <LineChart className="h-6 w-6" />
            </div>
            <h4 className="text-xl font-medium text-foreground mb-3">24/7 Lead Discovery</h4>
            <p className="text-muted-foreground">
              Need new customers? Vail can discover and qualify leads for you through GDPR-compliant methods, or work with your existing customer base as they contact you.
            </p>
          </div>
          
          <div className="subtle-card p-8 fade-in" style={{ transitionDelay: "100ms" }}>
            <div className="feature-icon mb-5">
              <BarChart3 className="h-6 w-6" />
            </div>
            <h4 className="text-xl font-medium text-foreground mb-3">24/7 Automated Workflows</h4>
            <p className="text-muted-foreground">
              After every conversation, Vail automatically handles next steps—whether that's landing a deal, satisfying a customer, or escalating to your team.
            </p>
          </div>
          
          <div className="subtle-card p-8 fade-in" style={{ transitionDelay: "200ms" }}>
            <div className="feature-icon mb-5">
              <Workflow className="h-6 w-6" />
            </div>
            <h4 className="text-xl font-medium text-foreground mb-3">Scale Without More Staff</h4>
            <p className="text-muted-foreground">
              Handle 10x the customer requests without hiring more people. Vail processes everything automatically and only escalates what truly needs human attention.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
